// Plan Id search for TblProductsSearch (#418): the plan an agreement already carries
// (findCurrentPlan), the plan an invoice line most likely meant (suggestPlan), and the search
// itself (buildPlanIndex once per catalog load, then searchPlans per keystroke). Codes and names
// are compared upper-case, letters and digits only.

const normalise = (value) => String(value ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// The catalog row for the plan an agreement already carries, so the Plan Id box can open with it
// picked and a dealer who is not changing the plan never has to touch the box.
//
// tblAgreement.productCode is not always a catalog code: the batch stores the code up to its
// first hyphen, plus "S" for sea-coast (jbw_st_dataTransformation), so HVAC-5YR is stored as
// HVAC. So, in order: an exact code; the agreement's productId, when that product's staged code
// agrees with what is stored (ids are not trusted alone); the staged code, when exactly one plan
// stages to it. Anything ambiguous returns null and the box simply shows the stored text.
// The staging rule's source of truth is jbwpotentialagreementaccept (stagedCode), which copies the
// batch's; this only reverses it to find the plan, and must change if that does.
const staged = (code) => String(code ?? "").split("-")[0];

export const findCurrentPlan = (products, productCode, productId) => {
  if (!productCode) return null;
  const exact = products.find((p) => p.productCode === productCode);
  if (exact) return exact;
  const stagesTo = (p) => staged(p.productCode) === productCode || `${staged(p.productCode)}S` === productCode;
  if (productId != null) {
    const byId = products.find((p) => String(p.productId) === String(productId));
    if (byId && stagesTo(byId)) return byId;
  }
  const candidates = products.filter(stagesTo);
  return candidates.length === 1 ? candidates[0] : null;
};

const words = (value) => String(value ?? "").toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);

// Built once per catalog load, not per keystroke.
export const buildPlanIndex = (products) => {
  const rows = products.map((p) => ({
    p,
    code: normalise(p.productCode),
    text: ` ${words(`${p.productCode} ${p.codeName}`).join(" ")} `,
  }));
  const byCode = new Map();
  for (const r of rows) byCode.set(r.code, [...(byCode.get(r.code) || []), r.p]);
  return { rows, byCode };
};

// The one active plan a normalised code names; null when none or several do.
const onlyPlan = (index, code) => {
  const hits = code ? index.byCode.get(code) : null;
  return hits?.length === 1 ? hits[0] : null;
};

// The plan an invoice line most likely meant, only when exactly one active plan fits: the whole
// SKU (JBALEPB150* -> JBALEPB150), else a space- or slash-separated token of it
// (JBALEHB500 LG -> JBALEHB500), else a JB code in the item name (... Heat Pump JBALTJB300).
export const suggestPlan = (index, sku, itemName) =>
  onlyPlan(index, normalise(sku)) ||
  String(sku ?? "").split(/[\s/]+/).map((t) => onlyPlan(index, normalise(t))).find(Boolean) ||
  (String(itemName ?? "").toUpperCase().match(/JB[A-Z0-9]{3,}/g) || [])
    .map((code) => onlyPlan(index, code))
    .find(Boolean) ||
  null;

// Code starts with the query, then code contains it, then every typed word is in the code or
// the plan name. If nothing matches, fall back to the longest prefix of the query that some code
// starts with, so JBALEHB500LG finds JBALEHB500 and JB5RY lists the JB5... plans.
export const searchPlans = (index, query, limit = 50) => {
  const q = normalise(query);
  const typed = words(query);
  if (!q) return index.rows.slice(0, limit).map((r) => r.p);
  const tiers = [[], [], []];
  for (const r of index.rows) {
    if (r.code.startsWith(q)) tiers[0].push(r.p);
    else if (r.code.includes(q)) tiers[1].push(r.p);
    else if (typed.every((w) => r.text.includes(` ${w}`))) tiers[2].push(r.p);
  }
  const hits = tiers.flat();
  if (hits.length) return hits.slice(0, limit);
  for (let n = q.length - 1; n >= 3; n--) {
    const prefix = q.slice(0, n);
    const near = index.rows.filter((r) => r.code.startsWith(prefix));
    if (near.length) return near.slice(0, limit).map((r) => r.p);
  }
  return [];
};
